/**
 * Error envelope handling — identity.yaml `ErrorEnvelope` + `x-error-codes`.
 *
 * Every non-2xx response carries `{ error: { code, message, details?, trace_id } }`.
 * The stable machine `code` drives client behavior and localization; the server
 * message is a fallback only (§11.4).
 */

import type { MessageKey } from "../i18n/messages";

export interface FieldError {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ReadonlyArray<FieldError>;
  readonly traceId: string | null;
  /** Seconds from the 429 `Retry-After` header (identity.yaml lockout/cooldown); null otherwise. */
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: ReadonlyArray<FieldError> = [],
    traceId: string | null = null,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.traceId = traceId;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Network-level failure (offline, DNS, CORS) — distinct from an API rejection. */
export class NetworkError extends Error {
  constructor() {
    super("Network request failed");
    this.name = "NetworkError";
  }
}

const KNOWN_CODES = [
  "VALIDATION_FAILED",
  "AUTH_INVALID_CREDENTIALS",
  "AUTH_TOKEN_INVALID",
  "AUTH_TOKEN_EXPIRED",
  "AUTH_ACCOUNT_SUSPENDED",
  "REFRESH_INVALID",
  "REFRESH_EXPIRED",
  "REFRESH_REUSE_DETECTED",
  "PERMISSION_DENIED",
  "RESOURCE_NOT_FOUND",
  "RATE_LIMITED",
  // geo.yaml x-error-codes (M2-14)
  "ZONE_NAME_TAKEN",
  "ZONE_ACTIVE",
  "ZONE_DISABLED",
  "BRANCH_LOCATION_MISSING",
  // merchants.yaml x-error-codes (M2-15)
  "OWNER_ALREADY_HAS_MERCHANT",
  "BRANCH_ZONE_INVALID",
  "STAFF_USER_NOT_FOUND",
  "STAFF_ALREADY_EMPLOYED",
  "MERCHANT_NOT_READY_FOR_ORDERS",
  // merchants.yaml Wave-8 addendum (M7-42, mock-first per broadcast 2026-08-17)
  "PHONE_TAKEN",
  "APPLICATION_ALREADY_DECIDED",
  "MULTI_BRANCH_DISABLED",
  // merchants.yaml v1.9.0 store media (Wave 16, M7-119). v1.12.0 retired
  // MENU_IMAGE_LIMIT — no server path can emit it (owner ruling 2026-09-15 #4).
  "MENU_ORDER_INCOMPLETE",
  // merchants.yaml v1.21.0 owner password reset (M7-326)
  "NO_OWNER_ACCOUNT",
  // theme.yaml (M6-03 theme management)
  "THEME_VERSION_CONFLICT",
  // orders.yaml (M6-04 order management)
  "INVALID_TRANSITION",
  "CANCELLATION_NOT_ALLOWED",
  "ORDER_NOT_EDITABLE",
  // dispatch.yaml (M6-05 quick assignment)
  "DRIVER_BUSY",
  "DRIVER_NOT_ELIGIBLE",
  // dispatch.yaml v1.3.0 (M7-81 re-dispatch) — a live wave is already out
  "DISPATCH_IN_PROGRESS",
  // promotions.yaml (M6-07 promotion management)
  "PROMOTION_NOT_EDITABLE",
  "PROMOTION_NOT_DELETABLE",
  "PROMOTION_STATUS_INVALID",
  "PROMOTION_KIND_MISMATCH",
  "FUNDING_SPLIT_INVALID",
  "COUPON_CODE_DUPLICATE",
  // settings.yaml (M6-07 platform config)
  "SETTING_VALUE_INVALID",
  "FLAG_NOT_MERCHANT_SCOPED",
  // payments.yaml (M6-07 provider registry)
  "PROVIDER_KEY_TAKEN",
  // identity.yaml RBAC admin (M7-23)
  "ROLE_KEY_TAKEN",
  "ROLE_SYSTEM_IMMUTABLE",
  "ROLE_IN_USE",
  // drivers.yaml deep driver management (M7-23)
  "DRIVER_USER_NOT_FOUND",
  "DRIVER_ALREADY_EXISTS",
  "DRIVER_BLOCK_REASON_RESERVED",
  "DOCUMENT_ALREADY_REVIEWED",
  // wallets.yaml driver plans (M7-23)
  "DRIVER_PLAN_INVALID",
  // marketing.yaml v1.0.0 (M7-106 ad banners)
  "BANNER_KIND_NOT_AVAILABLE",
  "BANNER_TARGET_NOT_FOUND",
  "BANNER_ORDER_INCOMPLETE",
  // drivers.yaml v1.5.0 (M7-150 admin-managed vehicle types)
  "VEHICLE_TYPE_IN_USE",
  "VEHICLE_TYPE_CODE_TAKEN",
  "VEHICLE_TYPE_ORDER_INCOMPLETE",
] as const;

type KnownCode = (typeof KNOWN_CODES)[number];

function isKnownCode(code: string): code is KnownCode {
  return (KNOWN_CODES as ReadonlyArray<string>).includes(code);
}

/** Maps a stable API error code to its localized message key (AR/EN). */
export function errorMessageKey(error: unknown): MessageKey {
  if (error instanceof NetworkError) return "error.offline";
  if (error instanceof ApiError && isKnownCode(error.code)) {
    return `apiError.${error.code}`;
  }
  return "apiError.UNKNOWN";
}

interface RawEnvelope {
  /** Typed 422 shape (M7-309 legal surface): field errors at the top level. */
  readonly errors?: unknown;
  readonly error?: {
    readonly code?: unknown;
    readonly message?: unknown;
    readonly details?: unknown;
    readonly trace_id?: unknown;
  };
}

/** Builds an ApiError from a non-2xx transport response body (+ consumed headers). */
export function toApiError(status: number, body: unknown, headers?: Readonly<Record<string, string>>): ApiError {
  const envelope = (typeof body === "object" && body !== null ? body : {}) as RawEnvelope;
  const raw = envelope.error;
  // A typed 422 `{ errors: [...] }` carries no `error` object — it is a
  // validation refusal by definition (M7-309).
  const topLevelErrors = raw === undefined && Array.isArray(envelope.errors) ? envelope.errors : null;
  const code = typeof raw?.code === "string" ? raw.code : topLevelErrors !== null ? "VALIDATION_FAILED" : "UNKNOWN";
  const message = typeof raw?.message === "string" ? raw.message : "Request failed";
  const traceId = typeof raw?.trace_id === "string" ? raw.trace_id : null;
  const rawDetails: unknown = Array.isArray(raw?.details) ? raw.details : topLevelErrors;
  const details = Array.isArray(rawDetails)
    ? rawDetails.filter(
        (d): d is FieldError =>
          typeof d === "object" &&
          d !== null &&
          typeof (d as FieldError).field === "string" &&
          typeof (d as FieldError).code === "string" &&
          typeof (d as FieldError).message === "string",
      )
    : [];
  const retryAfterRaw = headers?.["retry-after"];
  const retryAfterParsed = retryAfterRaw === undefined ? Number.NaN : Number.parseInt(retryAfterRaw, 10);
  const retryAfterSeconds = Number.isFinite(retryAfterParsed) && retryAfterParsed > 0 ? retryAfterParsed : null;
  return new ApiError(status, code, message, details, traceId, retryAfterSeconds);
}
